// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

// Structured address input: Street / City / State / ZIP
// Street field has live Nominatim autocomplete. Selecting a suggestion
// populates all four fields from the structured address data so the full
// street address is always available for precise geocoding.

import React, { useEffect, useRef, useState, useCallback } from "react";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN",
  "IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH",
  "NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT",
  "VT","VA","WA","WV","WI","WY",
];

const STATE_ABBREV: Record<string, string> = {
  "Alabama":"AL","Alaska":"AK","Arizona":"AZ","Arkansas":"AR","California":"CA",
  "Colorado":"CO","Connecticut":"CT","Delaware":"DE","Florida":"FL","Georgia":"GA",
  "Hawaii":"HI","Idaho":"ID","Illinois":"IL","Indiana":"IN","Iowa":"IA",
  "Kansas":"KS","Kentucky":"KY","Louisiana":"LA","Maine":"ME","Maryland":"MD",
  "Massachusetts":"MA","Michigan":"MI","Minnesota":"MN","Mississippi":"MS",
  "Missouri":"MO","Montana":"MT","Nebraska":"NE","Nevada":"NV","New Hampshire":"NH",
  "New Jersey":"NJ","New Mexico":"NM","New York":"NY","North Carolina":"NC",
  "North Dakota":"ND","Ohio":"OH","Oklahoma":"OK","Oregon":"OR","Pennsylvania":"PA",
  "Rhode Island":"RI","South Carolina":"SC","South Dakota":"SD","Tennessee":"TN",
  "Texas":"TX","Utah":"UT","Vermont":"VT","Virginia":"VA","Washington":"WA",
  "West Virginia":"WV","Wisconsin":"WI","Wyoming":"WY","District of Columbia":"DC",
};

interface Parts { street: string; city: string; state: string; zip: string; }

interface Suggestion {
  label: string;  // full formatted string shown in dropdown
  parts: Parts;   // parsed fields to populate on select
}

function parseAddress(addr: string): Parts {
  const m = addr.match(/^(.*?),\s*(.*?),?\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/);
  if (m) return { street: m[1].trim(), city: m[2].trim(), state: m[3], zip: m[4] };
  return { street: addr, city: "", state: "", zip: "" };
}

function combine(p: Parts): string {
  const parts = [p.street.trim()];
  const cityState = [p.city.trim(), p.state ? `${p.state}${p.zip ? " " + p.zip.trim() : ""}` : p.zip.trim()]
    .filter(Boolean).join(", ");
  if (cityState) parts.push(cityState);
  return parts.filter(Boolean).join(", ");
}

/** Extract structured Parts from a Nominatim address object + display_name. */
function nominatimToParts(addr: any): Parts | null {
  const num  = addr.house_number ?? "";
  const road = addr.road ?? addr.pedestrian ?? addr.footway ?? addr.path ?? addr.street ?? "";
  if (!road) return null; // skip results with no street

  const street = [num, road].filter(Boolean).join(" ");

  // City: prefer specific place name (village/hamlet) over administrative
  // township names. "Lower Merion Township" → fall back to addr.village.
  const rawCity: string = addr.city ?? "";
  const isTownship = /\b(township|twp|town of)\b/i.test(rawCity);
  const city: string = isTownship
    ? (addr.village ?? addr.hamlet ?? addr.suburb ?? addr.neighbourhood ?? addr.town ?? rawCity)
    : (rawCity || addr.town || addr.village || addr.hamlet || addr.suburb || "");

  const rawSC: string = addr.state_code ?? addr["ISO3166-2-lvl4"] ?? "";
  const state = rawSC.replace(/^US-/i, "").toUpperCase() || STATE_ABBREV[addr.state ?? ""] || "";
  const zip   = addr.postcode ? addr.postcode.split("-")[0] : "";

  return { street, city, state, zip };
}

interface Props {
  value: string;
  onChange: (combined: string) => void;
  /** Called with a clean geocoding string whenever city/state/zip change */
  onGeoQuery?: (query: string) => void;
  readOnly?: boolean;
  T: any;
}

export default function AddressFields({ value, onChange, onGeoQuery, readOnly, T }: Props) {
  const [parts, setParts]               = useState<Parts>(() => parseAddress(value));
  const [suggestions, setSuggestions]   = useState<Suggestion[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeIdx, setActiveIdx]       = useState(-1);
  const [fetchStatus, setFetchStatus]   = useState<"idle"|"fetching"|"empty"|"error">("idle");

  const lastValue       = useRef(value);
  const abortRef        = useRef<AbortController | null>(null);
  const debounceRef     = useRef<ReturnType<typeof setTimeout>>();
  const containerRef    = useRef<HTMLDivElement>(null);

  // Re-parse if parent changes the address externally (e.g. loading a saved project)
  useEffect(() => {
    if (value !== lastValue.current) {
      lastValue.current = value;
      setParts(parseAddress(value));
    }
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Nominatim fetch ────────────────────────────────────────────────────────
  const fetchSuggestions = useCallback((streetVal: string, city: string, state: string) => {
    const q = [streetVal.trim(), city.trim(), state.trim()].filter(Boolean).join(", ");
    if (q.length < 5) { setSuggestions([]); setDropdownOpen(false); return; }

    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setFetchStatus("fetching");

    const url =
      `https://nominatim.openstreetmap.org/search` +
      `?q=${encodeURIComponent(q)}&format=json&addressdetails=1&limit=6&countrycodes=us&email=openestimate%40app`;

    fetch(url, { signal: abortRef.current.signal, headers: { Accept: "application/json" } })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data: any[]) => {
        if (!Array.isArray(data) || data.length === 0) {
          setSuggestions([]); setFetchStatus("empty"); setDropdownOpen(true); return;
        }
        const seen = new Set<string>();
        const results: Suggestion[] = [];
        for (const item of data) {
          const p = nominatimToParts(item.address ?? {});
          if (!p) continue;
          const label = combine(p);
          if (!label || seen.has(label.toLowerCase())) continue;
          seen.add(label.toLowerCase());
          results.push({ label, parts: p });
        }
        setSuggestions(results);
        setFetchStatus(results.length === 0 ? "empty" : "idle");
        setDropdownOpen(true);
        setActiveIdx(-1);
      })
      .catch(err => {
        if (err.name === "AbortError") return;
        setSuggestions([]); setFetchStatus("error"); setDropdownOpen(true);
      });
  }, []);

  // ── Field update helpers ───────────────────────────────────────────────────
  const commitParts = (next: Parts) => {
    const combined = combine(next);
    lastValue.current = combined;
    onChange(combined);
    if (onGeoQuery) {
      const geoCity  = next.city.trim();
      const geoState = next.state;
      const geoZip   = next.zip.trim();
      if (geoCity || geoZip) {
        onGeoQuery([geoCity, geoState ? `${geoState}${geoZip ? " " + geoZip : ""}` : geoZip].filter(Boolean).join(", "));
      }
    }
  };

  const update = (field: keyof Parts, val: string) => {
    setParts(prev => {
      const next = { ...prev, [field]: val };
      commitParts(next);
      return next;
    });
  };

  const selectSuggestion = (s: Suggestion) => {
    setParts(s.parts);
    const combined = combine(s.parts);
    lastValue.current = combined;
    onChange(combined);
    // Fire onGeoQuery so climate-zone / map panels update immediately
    if (onGeoQuery && (s.parts.city || s.parts.zip)) {
      onGeoQuery(
        [s.parts.city, s.parts.state ? `${s.parts.state}${s.parts.zip ? " " + s.parts.zip : ""}` : s.parts.zip]
          .filter(Boolean).join(", ")
      );
    }
    setSuggestions([]);
    setDropdownOpen(false);
    setActiveIdx(-1);
    setFetchStatus("idle");
  };

  // ── Keyboard nav in dropdown ───────────────────────────────────────────────
  const handleStreetKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") { setDropdownOpen(false); return; }
    if (!dropdownOpen || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); selectSuggestion(suggestions[activeIdx]); }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const labelStyle: React.CSSProperties = {
    fontSize: 10, color: T.textMuted,
    letterSpacing: ".12em", textTransform: "uppercase" as any,
    marginBottom: 5, display: "block", fontFamily: FONT,
  };
  const inputStyle: React.CSSProperties = {
    width: "100%", boxSizing: "border-box" as any,
    background: T.bgInput, border: `1px solid ${T.borderSecondary}`,
    borderRadius: 3, color: T.textPrimary, fontFamily: FONT,
    fontSize: 12, padding: "6px 10px", outline: "none",
  };

  const showDropdown = dropdownOpen && !readOnly;

  return (
    <div style={{ display: "flex", flexDirection: "column" as any, gap: 10 }}>

      {/* Street — with autocomplete */}
      <div ref={containerRef} style={{ position: "relative" }}>
        <label style={labelStyle}>Street Address</label>
        <div style={{ position: "relative" }}>
          <input
            className="input-field"
            value={parts.street}
            onChange={e => {
              const val = e.target.value;
              update("street", val);
              clearTimeout(debounceRef.current);
              if (val.trim().length >= 4) {
                debounceRef.current = setTimeout(
                  () => fetchSuggestions(val, parts.city, parts.state),
                  380
                );
              } else {
                setSuggestions([]); setDropdownOpen(false); setFetchStatus("idle");
              }
            }}
            onKeyDown={handleStreetKeyDown}
            onFocus={() => { if (suggestions.length > 0) setDropdownOpen(true); }}
            readOnly={readOnly}
            placeholder="e.g. 4823 Ridgeline Drive"
            autoComplete="off"
            style={{ ...inputStyle, paddingRight: fetchStatus === "fetching" ? 28 : undefined }}
          />
          {fetchStatus === "fetching" && (
            <span style={{
              position: "absolute", right: 9, top: "50%", transform: "translateY(-50%)",
              fontSize: 13, color: T.textMuted, pointerEvents: "none",
            }}>⋯</span>
          )}
        </div>

        {/* Dropdown */}
        {showDropdown && (
          <div style={{
            position: "absolute", top: "calc(100% + 3px)", left: 0, right: 0,
            zIndex: 9999,
            background: T.bgPanel,
            border: `1px solid ${T.borderAccent}55`,
            borderRadius: 6,
            boxShadow: "0 8px 28px rgba(0,0,0,.38)",
            overflow: "hidden",
            maxHeight: 260,
            overflowY: "auto",
          }}>
            {fetchStatus === "error" && (
              <div style={{ padding: "9px 13px", fontFamily: FONT, fontSize: 11, color: T.textMuted, fontStyle: "italic" }}>
                Address lookup unavailable — type manually.
              </div>
            )}
            {fetchStatus === "empty" && (
              <div style={{ padding: "9px 13px", fontFamily: FONT, fontSize: 11, color: T.textMuted, fontStyle: "italic" }}>
                No matches — try adding city or ZIP.
              </div>
            )}
            {suggestions.map((s, i) => (
              <div
                key={i}
                onMouseDown={e => { e.preventDefault(); selectSuggestion(s); }}
                onMouseEnter={() => setActiveIdx(i)}
                style={{
                  padding: "8px 13px",
                  cursor: "pointer",
                  background: i === activeIdx ? `${T.borderAccent}22` : "transparent",
                  borderBottom: i < suggestions.length - 1 ? `1px solid ${T.borderSecondary}` : "none",
                  fontFamily: FONT,
                  fontSize: 12,
                  color: T.textSecondary,
                  lineHeight: 1.4,
                }}
              >
                {s.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* City / State / ZIP row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 100px", gap: 10 }}>
        <div>
          <label style={labelStyle}>City</label>
          <input
            className="input-field"
            value={parts.city}
            onChange={e => update("city", e.target.value)}
            readOnly={readOnly}
            placeholder="City"
            style={{ width: "100%", boxSizing: "border-box" as any }}
          />
        </div>
        <div>
          <label style={labelStyle}>State</label>
          <select
            value={parts.state}
            onChange={e => update("state", e.target.value)}
            disabled={readOnly}
            style={{
              ...inputStyle,
              cursor: readOnly ? "default" : "pointer",
              appearance: "auto" as any,
            }}
          >
            <option value="">—</option>
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>ZIP Code</label>
          <input
            className="input-field"
            value={parts.zip}
            onChange={e => update("zip", e.target.value.replace(/[^\d-]/g, ""))}
            readOnly={readOnly}
            placeholder="ZIP"
            maxLength={10}
            style={{ width: "100%", boxSizing: "border-box" as any }}
          />
        </div>
      </div>
    </div>
  );
}
